import { create } from 'zustand';
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware';

import { type Collapsing, type Filters } from '@@api/services/auth/schemas';
import { type ContentLocale } from '@@api/utils/schemas/schemas';
import { type SearchNamespace } from '@@containers/Search/types';
import { type ContentLanguage, LANG_CODES, type LangCode } from '@@lib/i18n/constants';

import { isMetadataContentLocaleRoute } from './utils';

const SETTINGS_STORE = 'settingsStore';

export type SettingsState = {
    contentLocale: ContentLanguage;
    betaFeaturesEnabled: boolean;
    collapsing: Collapsing | null;
    filters: Filters | null;
    uiLocale: LangCode;
    metadataContentLocale: ContentLanguage;
    pathname: string | undefined;
    arePreferencesLoaded: boolean;
};

type SettingsActions = {
    hydrateSettings: (preferences: Partial<SettingsState>) => void;
    setPreferencesLoaded: (arePreferencesLoaded: boolean) => void;
    setMetadataContentLocale: (locale: ContentLocale) => void;
    setPathname: (pathname: string | undefined) => void;
    clearSettings: () => void;
};

export const initialState: SettingsState = {
    contentLocale: LANG_CODES.GERMAN,
    betaFeaturesEnabled: false,
    collapsing: null,
    filters: null,
    uiLocale: LANG_CODES.ENGLISH,
    metadataContentLocale: LANG_CODES.GERMAN,
    pathname: undefined,
    arePreferencesLoaded: false,
};

export const useSettingsStore = create<SettingsState & SettingsActions>()(
    devtools(
        subscribeWithSelector(
            persist(
                (set) => ({
                    ...initialState,

                    hydrateSettings: (preferences) =>
                        set(
                            (state) => ({
                                ...state,
                                ...preferences,
                            }),
                            true,
                            'hydrateSettings',
                        ),

                    setPreferencesLoaded: (arePreferencesLoaded) =>
                        set({ arePreferencesLoaded }, false, 'setPreferencesLoaded'),

                    setMetadataContentLocale: (locale) =>
                        set({ metadataContentLocale: locale }, false, 'setMetadataContentLocale'),

                    setPathname: (pathname) => set({ pathname }, false, 'setPathname'),

                    clearSettings: () => set({ ...initialState }, false, 'clearSettings'),
                }),
                {
                    name: SETTINGS_STORE,
                    version: 1,
                    partialize: (state) => ({
                        contentLocale: state.contentLocale,
                        uiLocale: state.uiLocale,
                    }),
                },
            ),
        ),
        { name: SETTINGS_STORE, enabled: true },
    ),
);

export const selectContentLocale = (state: SettingsState) => {
    const isMetadataRoute = state.pathname && isMetadataContentLocaleRoute(state.pathname);
    if (isMetadataRoute && state.metadataContentLocale) {
        return state.metadataContentLocale;
    }

    if (state.contentLocale) {
        return state.contentLocale;
    }

    return LANG_CODES.GERMAN;
};

export const selectFilters = (namespace: SearchNamespace) => (state: SettingsState) => {
    const contentLocale = selectContentLocale(state);

    return state.filters?.[contentLocale]?.[namespace];
};

export const useContentLocale = () => useSettingsStore(selectContentLocale);

export const useNamespaceFilters = (namespace: SearchNamespace) =>
    useSettingsStore(selectFilters(namespace));
